/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  styled,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import logoIRM from 'assets/images/logoIRM.png';
import irmHeader from 'assets/images/irm-header.png';

// Styled components for A4-like prescription design
const PrescriptionContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '794px', // A4 width in pixels at 96dpi
  minHeight: '1123px', // A4 height in pixels at 96dpi
  margin: '0 auto',
  padding: '20px', // Reduced from 40px
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: '12px', // Reduced from 14px
  lineHeight: '1.4', // Reduced from 1.6
  color: '#333',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  '@media print': {
    boxShadow: 'none',
    margin: '0 !important',
    padding: '10mm 8mm !important', // Safe print margins: 10mm top/bottom, 8mm left/right
    minHeight: '277mm', // A4 height (297mm) minus margins (20mm)
    maxHeight: 'none',
    width: '210mm !important', // A4 width in mm
    maxWidth: '210mm !important',
    pageBreakInside: 'avoid',
    backgroundColor: '#ffffff !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
    fontSize: '11px !important', // Even smaller for print
    lineHeight: '1.3 !important',
    fontWeight: '500 !important', // Make all print text bolder
    '& *': {
      fontWeight: 'inherit !important',
    },
  },
}));

const HeaderImageSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '5px',
  paddingBottom: '5px',
  position: 'relative',
  '& .header-left': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0,
    flex: 1,
    paddingRight: '20px',
    borderRight: '2px solid #00798E',
    marginRight: '20px',
    '@media print': {
      borderRight: '2px solid #000000 !important',
    }
  },
  '& .header-right': {
    textAlign: 'right',
    minWidth: '250px',
    paddingLeft: '20px',
  },
  '& .logo-section': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '150px',
    paddingTop: '8px',
  },
  '& .logo': {
    width: '150px',
    height: 'auto',
    objectFit: 'contain',
    marginBottom: '5px',
  },
  '& .institute-info': {
    flex: 1,
    marginLeft: '15px',
    paddingTop: '5px',
  },
  '& .institute-name': {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#00798E',
    marginBottom: '6px',
    lineHeight: '1.2',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  '& .institute-subtitle': {
    fontSize: '11px',
    color: '#666',
    marginBottom: '4px',
    lineHeight: '1.2',
    fontWeight: '600',
    fontStyle: 'italic',
  },
  '& .contact-info': {
    fontSize: '10px',
    color: '#333',
    lineHeight: '1.3',
  },
  '& .doctor-info': {
    '& .doctor-name': {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '4px',
    },
    '& .doctor-qualification': {
      fontSize: '12px',
      color: '#666',
      marginBottom: '2px',
    },
    '& .doctor-specialization': {
      fontSize: '11px',
      color: '#666',
      marginBottom: '15px',
    },
  },
  '& .date-section': {
    borderTop: '1px solid #ccc',
    paddingTop: '10px',
    '& .date-label': {
      fontSize: '12px',
      color: '#333',
    },
  },
}));

// Commented out old header sections
/*
const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '15px',
  paddingBottom: '10px',
  borderBottom: '2px solid #00798E',
  position: 'relative',
  gap: '20px',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: '120px',
  position: 'relative',
  '& img': {
    maxHeight: '60px',
    width: 'auto',
    marginBottom: '8px',
  },
  // Horizontal lines below logo
  '&::after': {
    content: '""',
    width: '80px',
    height: '2px',
    backgroundColor: '#00798E',
    marginTop: '4px',
  },
}));

const VerticalDivider = styled(Box)(({ theme }) => ({
  width: '2px',
  height: '60px',
  backgroundColor: '#00798E',
  margin: '0 10px',
}));

const ClinicInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: 'left',
  '& .clinic-name': {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#00798E',
    marginBottom: '6px',
    lineHeight: '1.2',
  },
  '& .clinic-subtitle': {
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    marginBottom: '6px',
    lineHeight: '1.2',
  },
  '& .clinic-address': {
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.4',
  },
}));
*/

const PatientInfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: '8px', // More compact
  borderRadius: '4px',
  marginBottom: '10px', // Reduced spacing
  border: '1px solid #e0e0e0',
  '@media print': {
    padding: '6px !important',
    marginBottom: '8px !important',
  },
}));

const PrescriptionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px', // Reduced from 20px
  paddingBottom: '8px', // Reduced from 15px
  borderBottom: '2px solid #00798E',
}));

const VitalsCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f0f8ff',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '15px',
  border: '1px solid #b3d9ff',
}));

const ComponentSection = styled(Box)(({ theme }) => ({
  marginBottom: '15px', // Reduced from 25px
  '& .section-title': {
    fontSize: '14px', // Reduced from 18px
    fontWeight: 'bold',
    color: '#00798E',
    marginBottom: '8px', // Reduced from 15px
    paddingBottom: '4px', // Reduced from 8px
    borderBottom: '1px solid #e0e0e0', // Reduced from 2px
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      width: '3px', // Reduced from 4px
      height: '14px', // Reduced from 20px
      backgroundColor: '#4caf50',
      marginRight: '8px', // Reduced from 10px
      borderRadius: '2px',
    },
  },
}));

const MedicationTable = styled(Table)(({ theme }) => ({
  marginTop: '8px',
  '& .MuiTableHead-root': {
    backgroundColor: '#f8f9fa',
  },
  '& .MuiTableCell-head': {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#00798E',
    padding: '8px',
    borderBottom: '2px solid #e0e0e0',
  },
  '& .MuiTableCell-body': {
    fontSize: '10px',
    padding: '6px 8px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
  },
  '& .medication-name': {
    fontWeight: 'bold',
    color: '#333',
  },
}));

const MedicationItem = styled(Box)(({ theme }) => ({
  padding: '8px', // Reduced from 15px
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '4px', // Reduced from 6px
  marginBottom: '6px', // Reduced from 12px
  '&:last-child': {
    marginBottom: 0,
  },
  '& .medication-name': {
    fontSize: '13px', // Reduced from 16px
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '4px', // Reduced from 8px
  },
  '& .medication-details': {
    fontSize: '11px', // Reduced from 14px
    color: '#666',
    lineHeight: '1.3', // Reduced from 1.5
  },
}));

const InstructionBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeaa7',
  borderRadius: '4px', // Reduced from 6px
  padding: '10px', // Reduced from 15px
  marginTop: '12px', // Reduced from 25px
  '& .instruction-title': {
    fontSize: '13px', // Reduced from 16px
    fontWeight: 'bold',
    color: '#856404',
    marginBottom: '6px', // Reduced from 10px
  },
  '& .instruction-text': {
    fontSize: '11px', // Reduced from 14px
    color: '#856404',
    lineHeight: '1.4', // Reduced from 1.6
  },
}));

const SignatureSection = styled(Box)(({ theme }) => ({
  marginTop: '20px', // Reduced from 40px
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  '& .signature-box': {
    textAlign: 'center',
    minWidth: '150px', // Reduced from 200px
    '& .signature-line': {
      borderTop: '1px solid #333', // Reduced from 2px
      margin: '20px 0 6px 0', // Reduced spacing
    },
    '& .signature-label': {
      fontSize: '10px', // Reduced from 12px
      color: '#666',
      fontWeight: 'bold',
    },
  },
}));

const OpenPrescriptionSection = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  marginBottom: '20px',
  '& .prescription-lines': {
    minHeight: '300px',
    border: 'none',
    borderRadius: '0px',
    padding: '15px',
    backgroundColor: '#ffffff',
    // backgroundImage: `repeating-linear-gradient(
    //   transparent,
    //   transparent 24px,
    //   #e0e0e0 24px,
    //   #e0e0e0 25px
    // )`,
    lineHeight: '25px',
    fontSize: '12px',
    color: '#666',
    display: 'block',
    '@media print': {
      backgroundColor: '#ffffff !important',
      // backgroundImage: `repeating-linear-gradient(
      //   transparent,
      //   transparent 24px,
      //   #000000 24px,
      //   #000000 25px
      // ) !important`,
      color: 'transparent !important',
      minHeight: '400px !important',
    },
  },
}));

const WatermarkText = styled(Typography)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%) rotate(-45deg)',
  fontSize: '80px',
  fontWeight: 'bold',
  color: 'rgba(0, 121, 142, 0.05)',
  zIndex: 0,
  pointerEvents: 'none',
  userSelect: 'none',
  '@media print': {
    position: 'absolute',
    fontSize: '60px',
    color: 'rgba(0, 121, 142, 0.08) !important',
    '-webkit-print-color-adjust': 'exact',
    'color-adjust': 'exact',
  },
}));

function PrescriptionView() {
  const navigate = useNavigate();
  const { patientCode, coupleCode, prescriptionCode } = useParams();
  const componentRef = useRef();

  // State management
  const [loading, setLoading] = useState(true);
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [scanFiles, setScanFiles] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPrescriptionData();
  }, [prescriptionCode]);

  const loadPrescriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading prescription data for code:', prescriptionCode);

      // Fetch prescription details
      const response = await api.opd.getPrescriptionDetailsByCode(prescriptionCode);

      console.log('Prescription API response:', response);

      // Handle different response structures
      let prescriptionData = null;
      let patientData = null;

      if (response?.data) {
        // Check if response has success property (new format)
        if (response.data.success !== undefined) {
          if (response.data.success && response.data.data) {
            prescriptionData = response.data.data;
            patientData = response.data.data.patient;
          } else {
            setError(response.data.message || 'Failed to load prescription data');
            return;
          }
        } else {
          // Direct data format (old format)
          prescriptionData = response.data;
          patientData = response.data.patient;
        }

          if (prescriptionData) {
            setPrescriptionData(prescriptionData);
            console.log('Prescription data set:', prescriptionData);

            // Load scan files
            if (prescriptionData.id) {
              try {
                const filesResponse = await api.opd.getPrescriptionScanFiles(prescriptionData.id);
                if (filesResponse?.data) {
                  setScanFiles(filesResponse.data);
                  console.log('Scan files loaded:', filesResponse.data);
                }
              } catch (fileError) {
                console.error('Error loading scan files:', fileError);
                setScanFiles([]);
              }
            }          // Extract patient info from prescription data or fetch separately if needed
          if (patientData) {
            setPatientInfo(patientData);
            console.log('Patient info from prescription:', patientData);
          } else {
            // Fallback: Fetch patient info separately if not included
            console.log('Fetching patient info separately for code:', patientCode);
            try {
              const patientResponse = await api.contacts.getPatientDetailsByExternalCode(
                patientCode,
              );
              console.log('Patient API response:', patientResponse);

              if (patientResponse?.data) {
                let patientInfo = null;
                if (patientResponse.data.success !== undefined) {
                  patientInfo = patientResponse.data.success ? patientResponse.data.data : null;
                } else {
                  patientInfo = patientResponse.data;
                }

                if (patientInfo) {
                  setPatientInfo(patientInfo);
                  console.log('Patient info set separately:', patientInfo);
                }
              }
            } catch (patientError) {
              console.error('Error fetching patient info:', patientError);
              // Don't set error here, prescription can still be viewed without patient details
            }
          }
        } else {
          setError('No prescription data found');
        }
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('Error loading prescription:', error);
      setError(`Error loading prescription data: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Prescription_${prescriptionCode}`,
    onBeforeGetContent: () => {
      return Promise.resolve();
    },
    onAfterPrint: () => {
      console.log('Print completed');
    },
    removeAfterPrint: true,
  });

  // Fallback print function
  const handlePrintFallback = () => {
    if (componentRef.current) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Prescription ${prescriptionCode}</title>
            <style>
              body { 
                font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif; 
                margin: 0; 
                padding: 20px; 
                font-size: 14px; 
                line-height: 1.6; 
                color: #333;
              }
              @media print {
                body { margin: 0; padding: 10px; }
                .no-print { display: none; }
              }
              .watermark {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: 60px;
                font-weight: bold;
                color: rgba(0, 121, 142, 0.08);
                z-index: 0;
                pointer-events: none;
                user-select: none;
              }
            </style>
          </head>
          <body>
            ${componentRef.current.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // Main print handler that tries react-to-print first, then fallback
  const onPrintClick = () => {
    console.log('Print button clicked');
    console.log('Component ref:', componentRef.current);
    console.log('Prescription data:', prescriptionData);

    if (!componentRef.current) {
      console.error('Component ref is null');
      handlePrintFallback();
      return;
    }

    if (!prescriptionData) {
      console.error('No prescription data available');
      alert('No prescription data available to print');
      return;
    }

    try {
      if (handlePrint) {
        console.log('Using react-to-print');
        handlePrint();
      } else {
        console.log('Using fallback print');
        handlePrintFallback();
      }
    } catch (error) {
      console.error('Print error:', error);
      handlePrintFallback();
    }
  };

  const formatDate = (dateString) => {
    return formatDateDDMMYYYY(dateString) || 'N/A';
  };

  const formatDateTime = (dateString) => {
    return formatDateTimeDDMMYYYY(dateString) || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'default';
      case 'PUBLISHED':
        return 'success';
      case 'RECALLED':
        return 'error';
      case 'REPUBLISHED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatVisitNumber = (visitInfo) => {
    if (!visitInfo || !visitInfo.visit_no) return '';
    // Format visit number as 4-digit with # prefix
    const visitNo = visitInfo.visit_no.toString().padStart(4, '0');
    return `#${visitNo}`;
  };

  const getSpouseInfo = (patientInfo) => {
    if (!patientInfo) return null;

    // Try different possible paths for spouse information
    const spousePaths = [
      patientInfo?.spouse?.contact,       // New path based on API response
      patientInfo?.spouseContact,
      patientInfo?.patientCouple?.spouse_contact,
      patientInfo?.spouseCouple?.contact,
      patientInfo?.couple?.spouse_contact,
    ];

    for (const spouse of spousePaths) {
      if (spouse && spouse.full_name) {
        return spouse;
      }
    }

    return null;
  };

  const groupComponentsByType = (prescriptionData) => {
    if (!prescriptionData) return {};

    const grouped = {};

    // Handle different component types based on the actual API response structure
    if (prescriptionData.prescriptionMedications?.length > 0) {
      grouped['Medication'] = prescriptionData.prescriptionMedications.map((med) => ({
        ...med,
        type: 'Medication',
        name: med.medication_name || med.name,
        strength: med.strength,
        dosage: med.dosage,
        frequency: med.frequency,
        duration: med.duration,
        route: med.route,
        timing: med.timing,
        special_instructions: med.special_instructions,
      }));
    }

    if (prescriptionData.prescriptionInvestigations?.length > 0) {
      grouped['Investigation'] = prescriptionData.prescriptionInvestigations.map((inv) => ({
        ...inv,
        type: 'Investigation',
        name: inv.investigation_name || inv.name,
        special_instructions: inv.special_instructions,
      }));
    }

    if (prescriptionData.prescriptionObservations?.length > 0) {
      grouped['Observation'] = prescriptionData.prescriptionObservations.map((obs) => ({
        ...obs,
        type: 'Observation',
        name: obs.observation_name || obs.name,
        special_instructions: obs.special_instructions,
      }));
    }

    if (prescriptionData.prescriptionScans?.length > 0) {
      grouped['Scan'] = prescriptionData.prescriptionScans.map((scan) => ({
        ...scan,
        type: 'Scan',
        name: scan.scan_name || scan.name,
        special_instructions: scan.special_instructions,
      }));
    }

    if (prescriptionData.prescriptionTreatments?.length > 0) {
      grouped['Treatment'] = prescriptionData.prescriptionTreatments.map((treatment) => ({
        ...treatment,
        type: 'Treatment',
        name: treatment.treatment_name || treatment.name,
        special_instructions: treatment.special_instructions,
      }));
    }

    // Fallback: check if there's a components array (in case API structure changes)
    if (prescriptionData.components?.length > 0) {
      prescriptionData.components.forEach((component) => {
        const type = component.type || 'Other';
        if (!grouped[type]) {
          grouped[type] = [];
        }
        grouped[type].push(component);
      });
    }

    return grouped;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={loading} />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="h6" color="error">
                    {error}
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="logo"
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                  >
                    Go Back
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const groupedComponents = groupComponentsByType(prescriptionData);

  // Helper function to parse and format doctor qualifications
  const formatDoctorQualifications = (occupation) => {
    if (!occupation) return { degrees: '', specializations: '' };
    
    // Common degree patterns (typically shown on first line)
    const degreeKeywords = ['MBBS', 'MD', 'MS', 'DGO', 'DNB', 'MRCP', 'FRCP', 'DO', 'DM', 'MCh', 'BDS', 'MDS'];
    
    // Specialization patterns (typically shown on second line)  
    const specializationKeywords = ['FNB', 'Fellowship', 'Diploma', 'Certificate'];
    
    // Split by common delimiters
    const parts = occupation.split(/[,;]\s*/);
    
    const degrees = [];
    const specializations = [];
    
    parts.forEach(part => {
      const trimmedPart = part.trim();
      if (!trimmedPart) return;
      
      // Check if it's a degree
      const isDegree = degreeKeywords.some(keyword => 
        trimmedPart.toUpperCase().includes(keyword.toUpperCase())
      );
      
      // Check if it's a specialization
      const isSpecialization = specializationKeywords.some(keyword => 
        trimmedPart.toUpperCase().includes(keyword.toUpperCase())
      );
      
      if (isDegree && !isSpecialization) {
        degrees.push(trimmedPart);
      } else if (isSpecialization) {
        specializations.push(trimmedPart);
      } else {
        // If unclear, add to specializations (conservative approach)
        specializations.push(trimmedPart);
      }
    });
    
    return {
      degrees: degrees.join(', '),
      specializations: specializations.join(', ')
    };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {/* Action Bar */}
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 1 }}>
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Prescription View - {prescriptionCode}
                    </MDTypography>
                  </Box>

                  <Box display="flex" gap={1}>
                    <MDButton
                      variant="outlined"
                      color="white"
                      startIcon={<PrintIcon />}
                      onClick={onPrintClick}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: 'white',
                        },
                      }}
                    >
                      Print
                    </MDButton>
                  </Box>
                </Box>
              </MDBox>

              {/* Prescription Content */}
              <MDBox p={3}>
                <PrescriptionContainer ref={componentRef} elevation={3}>
                  {/* Main Content Wrapper */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  
                  {/* Watermark */}
                  {prescriptionData?.status !== 'DRAFT' && <WatermarkText>IRM</WatermarkText>}

                  {/* Header Section */}
                  <HeaderImageSection>
                    <Box className="header-left">
                      <Box className="logo-section">
                        <img src={logoIRM} alt="IRM Logo" className="logo" />
                      </Box>
                      <Box className="institute-info">
                        <Typography className="institute-name">
                          INSTITUTE OF REPRODUCTIVE MEDICINE
                        </Typography>
                        {/* <Typography className="institute-subtitle">
                          PIONEER IVF CENTRE IN INDIA
                        </Typography> */}
                        <Typography className="contact-info">
                          📧 bncirm@gmail.com / enquiry@irmkolkata.org<br />
                          🌐 www.irmkolkata.org
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="header-right">
                      {prescriptionData?.doctor_information && (
                        <Box className="doctor-info">
                          <Typography className="doctor-name">
                            {prescriptionData.doctor_information.name || 'Dr. Sunita Sharma'}
                          </Typography>
                          {(() => {
                            const { degrees, specializations } = formatDoctorQualifications(
                              prescriptionData.doctor_information.occupation
                            );
                            return (
                              <>
                                {degrees && (
                                  <Typography className="doctor-qualification">
                                    {degrees}
                                  </Typography>
                                )}
                                {specializations && (
                                  <Typography className="doctor-specialization">
                                    {specializations}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    </Box>
                  </HeaderImageSection>

                  {/* Blue Horizontal Divider Line */}
                  <Box 
                    sx={{
                      width: '100%',
                      height: '2px',
                      backgroundColor: '#00798E',
                      '@media print': {
                        backgroundColor: '#00798E !important',
                        '-webkit-print-color-adjust': 'exact',
                        'color-adjust': 'exact',
                      },
                    }}
                  />

                  {/* Two Column Layout with Vertical Divider */}
                  <Box sx={{ display: 'flex', gap: 0, flex: 1 }}>
                    {/* Left Section - 30% Vitals */}
                    <Box sx={{ width: '30%', pr: 2, pl: 1, pt: 1 }}>
                      {/* Basic Vitals - Vertical Display */}
                      <Box>
                        <Box mb={1.5}>
                          <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                            Blood Pressure:
                          </Typography>
                          {(prescriptionData?.bp_systolic && prescriptionData?.bp_diastolic) ? (
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.bp_systolic}/{prescriptionData.bp_diastolic} mmHg
                            </Typography>
                          ) : (
                            <Typography variant="body1" component="span" sx={{ fontSize: '11px', borderBottom: '1px solid #ccc', minWidth: '100px', display: 'inline-block' }}>
                              &nbsp;
                            </Typography>
                          )}
                        </Box>
                        
                        <Box mb={1.5}>
                          <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                            Weight:
                          </Typography>
                          {prescriptionData?.weight ? (
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.weight}
                            </Typography>
                          ) : (
                            <Typography variant="body1" component="span" sx={{ fontSize: '11px', borderBottom: '1px solid #ccc', minWidth: '80px', display: 'inline-block' }}>
                              &nbsp;
                            </Typography>
                          )}
                        </Box>
                        
                        <Box mb={1.5}>
                          <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                            Height:
                          </Typography>
                          {prescriptionData?.height ? (
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.height}
                            </Typography>
                          ) : (
                            <Typography variant="body1" component="span" sx={{ fontSize: '11px', borderBottom: '1px solid #ccc', minWidth: '80px', display: 'inline-block' }}>
                              &nbsp;
                            </Typography>
                          )}
                        </Box>
                        
                        <Box mb={1.5}>
                          <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                            BMI:
                          </Typography>
                          {prescriptionData?.bmi ? (
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.bmi}
                            </Typography>
                          ) : (
                            <Typography variant="body1" component="span" sx={{ fontSize: '11px', borderBottom: '1px solid #ccc', minWidth: '60px', display: 'inline-block' }}>
                              &nbsp;
                            </Typography>
                          )}
                        </Box>
                        
                        {prescriptionData?.chief_complaint && (
                          <Box mb={1.5}>
                            <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                              Chief Complaint:
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.chief_complaint}
                            </Typography>
                          </Box>
                        )}
                        
                        {prescriptionData?.diagnosis && (
                          <Box mb={1.5}>
                            <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#00798E', display: 'inline', mr: 1 }}>
                              Diagnosis:
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '11px', display: 'inline' }}>
                              {prescriptionData.diagnosis}
                            </Typography>
                          </Box>
                        )}

                        {(prescriptionData?.probable_diagonosis_female || prescriptionData?.probable_diagonosis_male) && (
                          <Box mt={2}>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '12px', fontWeight: 'bold', color: '#00798E', mb: 1 }}
                            >
                              Probable Diagnosis
                            </Typography>
                            {prescriptionData?.probable_diagonosis_female && (
                              <Box mb={1}>
                                <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#333', mb: 0.5 }}>
                                  Female
                                </Typography>
                                <Box
                                  sx={{ fontSize: '11px', color: '#333', lineHeight: 1.4, '& p, & ul, & ol': { margin: 0 } }}
                                  dangerouslySetInnerHTML={{ __html: prescriptionData.probable_diagonosis_female }}
                                />
                              </Box>
                            )}
                            {prescriptionData?.probable_diagonosis_male && (
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', color: '#333', mb: 0.5 }}>
                                  Male
                                </Typography>
                                <Box
                                  sx={{ fontSize: '11px', color: '#333', lineHeight: 1.4, '& p, & ul, & ol': { margin: 0 } }}
                                  dangerouslySetInnerHTML={{ __html: prescriptionData.probable_diagonosis_male }}
                                />
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Vertical Divider Line */}
                    <Box 
                      sx={{
                        width: '2px',
                        backgroundColor: '#00798E',
                        minHeight: '100%',
                        '@media print': {
                          backgroundColor: '#00798E !important',
                          '-webkit-print-color-adjust': 'exact',
                          'color-adjust': 'exact',
                        },
                      }}
                    />

                    {/* Right Section - 70% Content */}
                    <Box sx={{ width: '70%', pl: 2 }}>

                  {/* Date and Patient Info Section */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} mt={1}>
                    {/* Patient Information - Compact */}
                    <Box sx={{ flex: 1, pr: 2 }}>
                      <Grid container spacing={0.5}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            fontSize: '9px', 
                            fontWeight: '600',
                            display: 'inline',
                            lineHeight: 1.2,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            Patient Name: 
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontSize: '10px', 
                            fontWeight: '700',
                            display: 'inline',
                            ml: 0.5,
                            lineHeight: 1.3,
                            '@media print': { fontWeight: '800 !important' }
                          }}>
                            {patientInfo?.contact?.full_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            fontSize: '9px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 1.5,
                            lineHeight: 1.2,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            Code: 
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontSize: '10px', 
                            fontWeight: '700',
                            display: 'inline',
                            ml: 0.5,
                            lineHeight: 1.3,
                            '@media print': { fontWeight: '800 !important' }
                          }}>
                            {patientInfo?.contact?.external_code || patientCode}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            fontSize: '9px', 
                            fontWeight: '600',
                            display: 'inline',
                            lineHeight: 1.2,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            Gender: 
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontSize: '10px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 0.5,
                            lineHeight: 1.3,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            {patientInfo?.contact?.gender || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            fontSize: '9px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 1.5,
                            lineHeight: 1.2,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            Age: 
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontSize: '10px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 0.5,
                            lineHeight: 1.3,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            {patientInfo?.contact?.age || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ 
                            fontSize: '9px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 1.5,
                            lineHeight: 1.2,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            DOB: 
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontSize: '10px', 
                            fontWeight: '600',
                            display: 'inline',
                            ml: 0.5,
                            lineHeight: 1.3,
                            '@media print': { fontWeight: '700 !important' }
                          }}>
                            {formatDate(patientInfo?.contact?.date_of_birth)}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Spouse Information - Compact */}
                      {getSpouseInfo(patientInfo) && (
                        <Box mt={0.5}>
                          <Grid container spacing={0.5}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary" sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                display: 'inline',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                Spouse Name: 
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '700',
                                display: 'inline',
                                ml: 0.5,
                                lineHeight: 1.3,
                                '@media print': { fontWeight: '800 !important' }
                              }}>
                                {getSpouseInfo(patientInfo)?.full_name || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 1.5,
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                Code: 
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '700',
                                display: 'inline',
                                ml: 0.5,
                                lineHeight: 1.3,
                                '@media print': { fontWeight: '800 !important' }
                              }}>
                                {getSpouseInfo(patientInfo)?.external_code || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary" sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                display: 'inline',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                Gender: 
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 0.5,
                                lineHeight: 1.3,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                {getSpouseInfo(patientInfo)?.gender || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 1.5,
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                Age: 
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 0.5,
                                lineHeight: 1.3,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                {getSpouseInfo(patientInfo)?.age || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 1.5,
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                DOB: 
                              </Typography>
                              <Typography variant="body1" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '600',
                                display: 'inline',
                                ml: 0.5,
                                lineHeight: 1.3,
                                '@media print': { fontWeight: '700 !important' }
                              }}>
                                {formatDate(getSpouseInfo(patientInfo)?.date_of_birth)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>

                    {/* Patient/Couple/Spouse ID barcodes */}
                    <Box sx={{ px: 1 }}>
                      <PatientCodeBarcodes
                        patientCode={patientInfo?.contact?.external_code || patientCode}
                        coupleCode={coupleCode !== 'undefined' ? coupleCode : undefined}
                        spouseCode={getSpouseInfo(patientInfo)?.external_code}
                        documentCode={prescriptionData?.prescription_code || prescriptionCode}
                        documentLabel="Prescription No"
                      />
                    </Box>

                    {/* Date Section */}
                    <Box textAlign="right">
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#333' }}>
                        Date: {formatDate(prescriptionData?.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Patient Information */}
                  {/* <PatientInfoCard sx={{ display: 'none' }}>
                    <Typography
                      variant="h6"
                      sx={{ 
                        fontWeight: '700', 
                        color: '#00798E', 
                        mb: 0.5, 
                        fontSize: '12px',
                        '@media print': {
                          fontWeight: '800 !important',
                        }
                      }}
                    >
                      Patient Information
                    </Typography>
                    <Grid container spacing={0.5}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '9px', 
                          fontWeight: '600',
                          lineHeight: 1.2,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          Patient Name
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontSize: '10px', 
                          fontWeight: '700',
                          lineHeight: 1.3,
                          '@media print': { fontWeight: '800 !important' }
                        }}>
                          {patientInfo?.contact?.full_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '9px', 
                          fontWeight: '600',
                          lineHeight: 1.2,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          Code
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontSize: '10px', 
                          fontWeight: '700',
                          lineHeight: 1.3,
                          '@media print': { fontWeight: '800 !important' }
                        }}>
                          {patientInfo?.contact?.external_code || patientCode}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '9px', 
                          fontWeight: '600',
                          lineHeight: 1.2,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          Gender
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontSize: '10px', 
                          fontWeight: '600',
                          lineHeight: 1.3,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          {patientInfo?.contact?.gender || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '9px', 
                          fontWeight: '600',
                          lineHeight: 1.2,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          Age
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontSize: '10px', 
                          fontWeight: '600',
                          lineHeight: 1.3,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          {patientInfo?.contact?.age || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '9px', 
                          fontWeight: '600',
                          lineHeight: 1.2,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          DOB
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontSize: '10px', 
                          fontWeight: '600',
                          lineHeight: 1.3,
                          '@media print': { fontWeight: '700 !important' }
                        }}>
                          {formatDate(patientInfo?.contact?.date_of_birth)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Spouse Information - Show if available */}
                    {/* {getSpouseInfo(patientInfo) && (
                      <Box mt={1}>
                        <Typography
                          variant="h6"
                          sx={{ 
                            fontWeight: '700', 
                            color: '#00798E', 
                            mb: 0.5, 
                            fontSize: '12px',
                            '@media print': {
                              fontWeight: '800 !important',
                            }
                          }}
                        >
                          Spouse Information
                        </Typography>
                        <Grid container spacing={0.5}>
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}
                            >
                              Spouse Name
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '700',
                              lineHeight: 1.3,
                              '@media print': { fontWeight: '800 !important' }
                            }}>
                              {getSpouseInfo(patientInfo)?.full_name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}
                            >
                              Code
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '700',
                              lineHeight: 1.3,
                              '@media print': { fontWeight: '800 !important' }
                            }}>
                              {getSpouseInfo(patientInfo)?.external_code || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}
                            >
                              Gender
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '600',
                              lineHeight: 1.3,
                              '@media print': { fontWeight: '700 !important' }
                            }}>
                              {getSpouseInfo(patientInfo)?.gender || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}
                            >
                              Age
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '600',
                              lineHeight: 1.3,
                              '@media print': { fontWeight: '700 !important' }
                            }}>
                              {getSpouseInfo(patientInfo)?.age || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ 
                                fontSize: '9px', 
                                fontWeight: '600',
                                lineHeight: 1.2,
                                '@media print': { fontWeight: '700 !important' }
                              }}
                            >
                              DOB
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '600',
                              lineHeight: 1.3,
                              '@media print': { fontWeight: '700 !important' }
                            }}>
                              {formatDate(getSpouseInfo(patientInfo)?.date_of_birth)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </PatientInfoCard> */}

                  {/* Prescription Header */}
                  {/* <PrescriptionHeader>
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 'bold', color: '#00798E', fontSize: '16px' }}
                      >
                        PRESCRIPTION {formatVisitNumber(prescriptionData?.visit_information)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                        Code: {prescriptionData?.prescription_code} | Type: {prescriptionData?.type}
                        {prescriptionData?.visit_information && (
                          <span>
                            {' '}
                            | Visit: {formatVisitNumber(prescriptionData.visit_information)}
                          </span>
                        )}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Chip
                        label={prescriptionData?.status}
                        color={getStatusColor(prescriptionData?.status)}
                        size="small"
                        sx={{ mb: 0.5, fontSize: '10px' }}
                      />
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                        Date: {formatDate(prescriptionData?.created_at)}
                      </Typography>
                    </Box>
                  </PrescriptionHeader> */}

                  {/* Rx Symbol */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: '48px',
                        fontWeight: 'bold',
                        color: '#00798E',
                        fontFamily: 'serif',
                        lineHeight: 1,
                        marginRight: '15px',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                      }}
                    >
                      ℞
                    </Typography>
                    <Box
                      flex={1}
                      height="2px"
                      bgcolor="#e0e0e0"
                      sx={{
                        background: 'linear-gradient(to right, #00798E 0%, #e0e0e0 100%)',
                        borderRadius: '1px',
                      }}
                    />
                  </Box>

                  {/* Basic Vitals Information - Hidden */}
                  {/* <VitalsCard>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#00798E', mb: 1, fontSize: '14px' }}
                    >
                      Basic Vitals & Clinical Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                          Blood Pressure
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '11px' }}>
                          {prescriptionData?.bp_systolic && prescriptionData?.bp_diastolic
                            ? `${prescriptionData.bp_systolic}/${prescriptionData.bp_diastolic} mmHg`
                            : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                          Weight
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '11px' }}>
                          {prescriptionData?.weight || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                          Height
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '11px' }}>
                          {prescriptionData?.height || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '10px' }}>
                          BMI
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '11px' }}>
                          {prescriptionData?.bmi || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                    {(prescriptionData?.chief_complaint ||
                      prescriptionData?.diagnosis ||
                      prescriptionData?.remarks) && (
                      <Box mt={1}>
                        <Grid container spacing={1}>
                          {prescriptionData?.chief_complaint && (
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ fontSize: '10px' }}
                              >
                                Chief Complaint
                              </Typography>
                              <Typography variant="body1" sx={{ fontSize: '11px' }}>
                                {prescriptionData.chief_complaint}
                              </Typography>
                            </Grid>
                          )}
                          {prescriptionData?.diagnosis && (
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ fontSize: '10px' }}
                              >
                                Diagnosis
                              </Typography>
                              <Typography variant="body1" sx={{ fontSize: '11px' }}>
                                {prescriptionData.diagnosis}
                              </Typography>
                            </Grid>
                          )}
                          {prescriptionData?.remarks && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ fontSize: '10px' }}
                              >
                                Remarks
                              </Typography>
                              <Box
                                dangerouslySetInnerHTML={{ __html: prescriptionData.remarks }}
                                sx={{
                                  fontSize: '11px',
                                  '& p, & ul, & ol': {
                                    margin: 0,
                                  },
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </VitalsCard>
                  */}

                  {/* Prescription Components */}
                  {Object.keys(groupedComponents).length > 0 ? (
                    Object.keys(groupedComponents).map((componentType) => (
                      <ComponentSection key={componentType}>
                        <Typography className="section-title">{componentType}s</Typography>

                        {componentType === 'Medication' ? (
                          <TableContainer
                            component={Paper}
                            sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}
                          >
                            <MedicationTable size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Medication Name
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Strength
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Dosage
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Frequency
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Duration
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Route
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Timing
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Instructions
                                  </TableCell>
                                </TableRow>
                                {groupedComponents[componentType].map((component, index) => (
                                  <TableRow key={component.id || index}>
                                    <TableCell className="medication-name">
                                      {component.name}
                                    </TableCell>
                                    <TableCell>{component.strength || '-'}</TableCell>
                                    <TableCell>{component.dosage || '-'}</TableCell>
                                    <TableCell>{component.frequency || '-'}</TableCell>
                                    <TableCell>{component.duration || '-'}</TableCell>
                                    <TableCell>{component.route || '-'}</TableCell>
                                    <TableCell>{component.timing || '-'}</TableCell>
                                    <TableCell>{component.special_instructions || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </MedicationTable>
                          </TableContainer>
                        ) : (
                          <TableContainer
                            component={Paper}
                            sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}
                          >
                            <MedicationTable size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    {componentType} Name
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 'bold !important',
                                      color: '#00798E  !important',
                                    }}
                                  >
                                    Instructions
                                  </TableCell>
                                </TableRow>
                                {groupedComponents[componentType].map((component, index) => (
                                  <TableRow key={component.id || index}>
                                    <TableCell className="medication-name">
                                      {component.name}
                                    </TableCell>
                                    <TableCell>{component.special_instructions || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </MedicationTable>
                          </TableContainer>
                        )}
                      </ComponentSection>
                    ))
                  ) : prescriptionData?.status === 'DRAFT' ? (
                    <OpenPrescriptionSection>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', color: '#00798E', mb: 2, fontSize: '14px' }}
                      >
                        Prescription
                      </Typography>
                      {prescriptionData?.synopsis === null ? (
                      <Box className="prescription-lines"></Box>
                      ) : (null)}
                    </OpenPrescriptionSection>
                  ) : null}

                  {/* General Instructions */}
                  {prescriptionData?.instructions && (
                    <InstructionBox>
                      <Typography className="instruction-title">General Instructions</Typography>
                      <Typography className="instruction-text">
                        {prescriptionData.instructions}
                      </Typography>
                    </InstructionBox>
                  )}

                  {/* Additional Notes */}
                  {prescriptionData?.notes && (
                    <InstructionBox sx={{ backgroundColor: '#e8f5e8', borderColor: '#c3e6c3' }}>
                      <Typography className="instruction-title" sx={{ color: '#2e7d32' }}>
                        Additional Notes
                      </Typography>
                      <Typography className="instruction-text" sx={{ color: '#2e7d32' }}>
                        {prescriptionData.notes}
                      </Typography>
                    </InstructionBox>
                  )}

                  {/* Synopsis Section */}
                  {prescriptionData?.synopsis && (
                    <Box sx={{ mb: 2 }}>
                      {/* <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, fontSize: '13px', mb: 1, color: '#333' }}
                      >
                        Synopsis
                      </Typography> */}
                      <Box
                        dangerouslySetInnerHTML={{ __html: prescriptionData.synopsis }}
                        sx={{
                          fontSize: '12px',
                          color: '#555',
                          lineHeight: 1.6,
                          '& p, & ul, & ol': {
                            margin: 0,
                            marginBottom: '8px',
                          },
                          '& p:last-child, & ul:last-child, & ol:last-child': {
                            marginBottom: 0,
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Prescription Scan Files Section */}
                  {/* {scanFiles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#00798E',
                          mb: 1,
                          textAlign: 'center',
                          backgroundColor: '#f8f9fa',
                          padding: '6px',
                          borderRadius: '4px',
                        }}
                      >
                        Attached Files ({scanFiles.length})
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: '#f0f8ff',
                          padding: '12px',
                          borderRadius: '6px',
                          border: '1px solid #b3d9ff',
                        }}
                      >
                        <Grid container spacing={1}>
                          {scanFiles.map((file, index) => (
                            <Grid item xs={12} key={file.id}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      color: '#00798E',
                                      mr: 1,
                                    }}
                                  >
                                    {index + 1}.
                                  </Typography>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: '10px', fontWeight: 500 }}
                                    >
                                      {file.file_name}
                                    </Typography>
                                    {file.description && (
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: '9px', color: '#666' }}
                                      >
                                        {file.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '9px', color: '#666' }}
                                >
                                  {(file.file_size / 1024).toFixed(2)} KB
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  )} */}

                  {/* Signature Section */}
                  {/* <SignatureSection>
                    <Box className="signature-box">
                      <Typography variant="body2" sx={{ fontSize: '10px' }}>
                        <strong>Date:</strong> {formatDate(prescriptionData?.created_at)}
                      </Typography>
                    </Box>

                    <Box className="signature-box">
                      <Box className="signature-line"></Box>
                      <Typography className="signature-label">Doctor&apos;s Signature</Typography>
                    </Box>
                  </SignatureSection> */}

                    </Box>
                  </Box>
                  
                  </Box>
                  {/* End Main Content Wrapper */}

                  {/* Footer */}
                  <Box pt={2} mt="auto" borderTop="2px solid #00798E" textAlign="center">
                    <Typography variant="body2" sx={{ fontSize: '10px', color: '#333', mb: 0.5 }}>
                      HB 36/A/3, SECTOR III, SALT LAKE, KOLKATA - 700106 ☎ 91 - 33 - 2321 - 5125/27
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '10px', color: '#333', mb: 0.5 }}>
                      DD 18/5/1, SECTOR - I, SALT LAKE, KOLKATA - 700064 ☎ 91 - 33 - 2334 - 1547
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '10px', color: '#333', mb: 1 }}>
                      Emergency Contact Number : 8420920232
                    </Typography>
                    {/* <Typography variant="body2" color="textSecondary" sx={{ fontSize: '9px', fontStyle: 'italic' }}>
                      Computer-generated prescription | Generated: {formatDateTime(new Date())}
                    </Typography> */}
                  </Box>
                </PrescriptionContainer>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PrescriptionView;
